import React, { useState, useEffect, useRef } from "react";
import { Quote, Award, ChevronRight, Play, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Rector = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showBio, setShowBio] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const videoCloseRef = useRef(null);
  const bioCloseRef = useRef(null);

  const biography = `
Engr. Dr. Baba Aliyu Haruna was born on January 6th, 1967 in Bida, Niger State. 
He obtained his B.Eng in Chemical Engineering from Ahmadu Bello University, Zaria, 
M.Sc in Biochemical Engineering from University of Birmingham (UK) in 1992 
and a PhD in Chemical Engineering from Federal University of Technology, Minna in 2017.

He joined the Federal Polytechnic Bida in 1990 as an Assistant Lecturer and rose to Chief Lecturer in 2009.
He has served as Director Internal Quality Assurance, Director Linkages and Collaboration,
Director of Continuing Education, Director MIS, Head of Department, and Chairman of ASUP (Bida Chapter).

He is a Fellow of COREN, FNSE, FNSChE and a member of several other professional bodies.
Dr. Baba Aliyu Haruna has numerous publications and has contributed immensely to engineering education 
and youth development through mentorship, consultancy, and community service.

Under his leadership, the Federal Polytechnic Bida has continued to excel in academic excellence, 
research innovation, and industry partnerships, positioning the institution as a leading polytechnic in Nigeria.
  `;

  const fullMessage = `
Welcome to Federal Polytechnic Bida, where we have been nurturing technical excellence and innovation for over four decades. Our commitment to providing world-class polytechnic education has made us a beacon of hope for aspiring technologists and engineers across Nigeria and beyond.

At Federal Polytechnic Bida, we believe in transforming lives through quality education, practical skills development, and character formation. Our state-of-the-art facilities, experienced faculty, and industry partnerships ensure that our graduates are not just job seekers, but job creators and industry leaders.

As you explore our institution, you will discover a vibrant community dedicated to academic excellence, research, and innovation. We invite you to be part of this transformative journey.

Welcome once again to Federal Polytechnic Bida - where excellence meets opportunity!
  `;

  // Handle ESC key for both modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isVideoPlaying) setIsVideoPlaying(false);
        if (showBio) setShowBio(false);
      }
    };

    if (isVideoPlaying || showBio) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
      
      // Focus close button
      setTimeout(() => {
        if (isVideoPlaying) videoCloseRef.current?.focus();
        if (showBio) bioCloseRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isVideoPlaying, showBio]);

  return (
    <section 
      className="relative py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50"
      aria-labelledby="rector-heading"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-5">
            <Award className="w-4 h-4 mr-2" />
            Leadership Message
          </div>
          <h2 
            id="rector-heading"
            className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-4 leading-tight"
          >
            A Message from our{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Rector
            </span>
          </h2>
        </motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Rector Photo Card */}
          <motion.div
            className="relative group"
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <div className="absolute -top-8 -left-8 w-36 h-36 bg-blue-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />
            <div className="absolute -bottom-8 -right-8 w-28 h-28 bg-purple-200 rounded-full opacity-20 group-hover:opacity-30 transition" aria-hidden="true" />

            <div className="relative bg-white/80 backdrop-blur-xl border border-gray-100 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-6">
              <div className="relative overflow-hidden rounded-2xl">
                {/* Image with loading state */}
                {!imageLoaded && !imageError && (
                  <div className="w-full aspect-[4/3] bg-gray-200 rounded-2xl animate-pulse" />
                )}
                
                {imageError && (
                  <div className="w-full aspect-[4/3] bg-gray-100 rounded-2xl flex items-center justify-center">
                    <p className="text-gray-500 text-sm">Image not available</p>
                  </div>
                )}

                <img
                  src="/images/rector.jpg"
                  alt="Engr. Dr. Baba Aliyu Haruna, Rector of Federal Polytechnic Bida"
                  className={`w-full aspect-[4/3] object-cover rounded-2xl transition-opacity duration-300 ${
                    imageLoaded ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                />

                {imageLoaded && !imageError && (
                  <>
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-2xl" />

                    {/* Play button overlay */}
                    <button
                      onClick={() => setIsVideoPlaying(true)}
                      aria-label="Play welcome message video from the Rector"
                      className="absolute inset-0 flex items-center justify-center group/play focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-2xl"
                    >
                      <div className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:shadow-2xl transition group-hover/play:scale-110">
                        <Play className="w-8 h-8 text-blue-600 ml-1" />
                      </div>
                    </button>
                  </>
                )}
              </div>

              <div className="mt-6 text-center">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">Engr. Dr. Baba Aliyu Haruna</h3>
                <p className="text-blue-600 font-semibold">Rector, Federal Polytechnic Bida</p>
                <p className="text-gray-500 text-sm">Ph.D. Engineering, M.Eng., B.Eng.</p>
              </div>
            </div>
          </motion.div>

          {/* Message Card */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <Quote className="absolute -top-6 -left-6 w-14 h-14 text-blue-100" aria-hidden="true" />
            <div className="relative z-10 bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10 hover:shadow-2xl transition">
              <blockquote className="text-justify text-base sm:text-lg text-gray-700 leading-relaxed mb-8">
                "{fullMessage.trim().split('\n\n')[0]}..."
              </blockquote>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="font-bold text-gray-900">Engr. Dr. Baba Aliyu Haruna</p>
                  <p className="text-gray-600 text-sm">Rector & Chief Executive</p>
                </div>
                <button
                  onClick={() => setShowBio(true)}
                  aria-label="Read full biography of Engr. Dr. Baba Aliyu Haruna"
                  className="inline-flex items-center justify-center px-5 py-2 rounded-full bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Read Full Biography
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {isVideoPlaying && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsVideoPlaying(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="video-modal-title"
          >
            <motion.div
              className="relative bg-white rounded-3xl p-4 sm:p-6 w-full max-w-4xl"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                ref={videoCloseRef}
                onClick={() => setIsVideoPlaying(false)}
                aria-label="Close video modal"
                className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center z-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>

              <h3 id="video-modal-title" className="text-xl font-bold text-gray-900 mb-4">
                Message from the Rector
              </h3>
              <div className="aspect-video rounded-2xl overflow-hidden bg-gray-900">
                <iframe
                  src="https://www.youtube.com/embed/LQrABe3-Pco"
                  title="Rector's Welcome Message - Engr. Dr. Baba Aliyu Haruna"
                  className="w-full h-full"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <p className="text-sm text-gray-600 mt-4 text-center">
                Press ESC or click outside to close
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Biography Modal */}
      <AnimatePresence>
        {showBio && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowBio(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bio-modal-title"
          >
            <motion.div
              className="relative bg-white rounded-3xl p-6 sm:p-8 w-full max-w-3xl max-h-[80vh] overflow-y-auto"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                ref={bioCloseRef}
                onClick={() => setShowBio(false)}
                aria-label="Close biography modal"
                className="sticky top-0 float-right w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center z-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>

              <h3 id="bio-modal-title" className="text-center text-xl sm:text-2xl font-bold text-gray-900 mb-6 pr-10">
                Biography of Engr. Dr. Baba Aliyu Haruna
              </h3>
              <p className="text-justify text-gray-700 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                {biography}
              </p>
              <p className="text-sm text-gray-600 mt-6 text-center">
                Press ESC or click outside to close
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Rector;